<template>
    <!-- 选择规格 -->
    <a-row :gutter="24">
      <a-col :span="24">
        <div v-if="product.kind === 'point'">
          <h3>选择套餐</h3>
          <a-radio-group v-model:value="selectedPointSku" button-style="solid" @change="handlePointSkuChange">
            <a-radio-button
                v-for="sku in kind_product.product_point_skus"
                :key="sku.id"
                :value="sku"
            >
              {{ sku.quantity }}{{ kind_product.point_name }} - ¥{{ sku.price.toFixed(2) }}
            </a-radio-button>
          </a-radio-group>
          <p style="margin-top: 16px">
            购买数量：
            <a-input-number
                id="buy_num"
                v-model:value="buyNum"
                :min="1"
                :precision="0"
                @change="calculateGain()"
            />
            <span>&ensp;{{ gain }}</span>
          </p>
        </div>

        <div v-else-if="product.kind === 'subscription'">
          <h3>选择套餐</h3>
          <a-radio-group v-model:value="selectedSubscriptionSku" button-style="solid" @change="handleSubscriptionSkuChange">
            <a-radio-button
                v-for="sku in kind_product.product_subscription_skus"
                :key="sku.id"
                :value="sku"
            >
              <template v-if="sku.forever">
                永久 - ¥{{ sku.price.toFixed(2) }}
              </template>
              <template v-else>
                {{ sku.year > 0 ? `${sku.year}年` : '' }}
                {{ sku.month > 0 ? `${sku.month}月` : '' }}
                {{ sku.day > 0 ? `${sku.day}天` : '' }}
                - ¥{{ sku.price.toFixed(2) }}
              </template>
            </a-radio-button>
          </a-radio-group>
          <p style="margin-top: 16px" v-if="!selectedSubscriptionSku?.forever">
            购买数量：
            <a-input-number
                id="buy_num"
                v-model:value="buyNum"
                :min="1"
                :precision="0"
                @change="calculateGain()"
            />
            <span>&ensp;{{ gain }}</span>
          </p>
        </div>
      </a-col>
    </a-row>
    <!-- 支付方式 -->
    <a-row :gutter="24" class="pay-methods-row">
      <a-col :span="24">
        <div class="pay-methods">
          <h3>立即购买</h3>
          <template v-if="payMethods.length > 0">
            <ul class="pay-methods-list">
              <li
                  v-for="method in payMethods"
                  :key="method.provider_id"
                  class="pay-method-item"
              >
                <img
                    :src="method.logo"
                    :alt="method.Name"
                    class="pay-method-logo"
                    @click="handlePayMethodClick(method.provider_id, method.pay_env)"
                />
              </li>
            </ul>
            <a-modal
                v-model:open="payModalVisible"
                title="扫码支付"
                :footer="null"
                :closable="false"
            >
              <div style="text-align: center">
                <p style="width: 300px; margin: auto;"> <a-qrcode :value="payQRCodeUrl" size="300" /></p>
                <p style="margin-top: 16px">请使用手机扫描二维码完成支付</p>
              </div>
            </a-modal>
          </template>
          <template v-else>暂未开通购买渠道</template>
        </div>
      </a-col>
    </a-row>
</template>

<script>
import {defineComponent, onMounted, onUnmounted, ref} from 'vue';
import request from '@/utils/request';
import imageErr from "@/assets/image_err.png";
import {message} from "ant-design-vue";

export default defineComponent({
  name: 'BuyProduct',
  props: {
    product: {
      type: Object,
      default: () => ({})
    },
    kind_product: {
      type: Object,
      default: () => ({
        content: ''
      })
    },
  },
  emits: ['usableChange'],
  setup(props, { emit }) {
    const payMethods = ref([]);

    const buyNum = ref(1);
    const gain = ref('');
    const selectedPointSku = ref(null);
    const selectedSubscriptionSku = ref(null);

    const payModalVisible = ref(false); // 支付弹窗可见性
    const payQRCodeUrl = ref(''); // 支付二维码URL
    const tradeNo = ref(''); // 交易号
    const checkPayInterval = ref(null); // 轮询定时器

    // 获取支付方式
    const fetchPayMethods = async () => {
      try {
        const response = await request.get('/trade/pay_method/pc');
        payMethods.value = response.data.rows || [];
        initSku()
      } catch (err) {
        console.error('获取支付方式失败:', err);
      }
    };

    const initSku = () => {
      if (props.product.kind === 'point') {
        // 默认选中第一个点数SKU
        if (props.kind_product.product_point_skus?.length > 0) {
          selectedPointSku.value = props.kind_product.product_point_skus[0];
        }
      } else if (props.product.kind === 'subscription') {
        // 默认选中第一个订阅SKU
        if (props.kind_product.product_subscription_skus?.length > 0) {
          selectedSubscriptionSku.value = props.kind_product.product_subscription_skus[0];
        }
      }
    };

    // 计算收益
    // 处理点数SKU选择变化
    const handlePointSkuChange = () => {
      buyNum.value = 1;
      calculateGain();
    };

    // 处理订阅SKU选择变化
    const handleSubscriptionSkuChange = () => {
      buyNum.value = 1;
      calculateGain();
    };

    const calculateGain = () => {
      if (props.product.kind === 'point' && selectedPointSku.value) {
        gain.value = `购买后可获得${buyNum.value * selectedPointSku.value.quantity} ${props.kind_product.point_name}，总价：¥${(buyNum.value * selectedPointSku.value.price).toFixed(2)}`;
      } else if (props.product.kind === 'subscription' && selectedSubscriptionSku.value) {
        if (selectedSubscriptionSku.value.forever) {
          gain.value = `购买后可永久使用，价格：¥${selectedSubscriptionSku.value.price.toFixed(2)}`;
          return;
        }
        let year = buyNum.value * selectedSubscriptionSku.value.year;
        let month = buyNum.value * selectedSubscriptionSku.value.month;
        let day = buyNum.value * selectedSubscriptionSku.value.day;
        // 处理天数进位到月份
        const daysPerMonth = 31; // 假设每月31天
        if (day >= daysPerMonth) {
          const additionalMonths = Math.floor(day / daysPerMonth);
          month += additionalMonths;
          day = day % daysPerMonth;
        }
        // 处理月份进位到年份
        if (month >= 12) {
          const additionalYears = Math.floor(month / 12);
          year += additionalYears;
          month = month % 12;
        }

        gain.value = `购买后可使用`
        if (year > 0) {
          gain.value += `${year}年`;
        }
        if (month > 0) {
          gain.value += `${month}月`;
        }
        if (day > 0) {
          gain.value += `${day}天`;
        }
      }
    }

    // 处理支付方式点击
    const handlePayMethodClick = async (providerId, payEnv) => {
      try {
        let data = {
          product_id: props.product.id,
          kind: props.product.kind,
          pay_provider: {
            provider_id: providerId,
            pay_env: payEnv
          }
        }
        if (props.product.kind === 'text') {
          data.text = {
            kind_product_id: props.product.kind_product_id,
          }
        } else if (props.product.kind === 'point') {
          if (!selectedPointSku.value) {
            message.error('请选择SKU');
            return;
          }
          data.point = {
            kind_product_id: props.product.kind_product_id,
            sku_id: selectedPointSku.value.id,
            num: buyNum.value,
          }
        } else if (props.product.kind === 'subscription') {
          if (!selectedSubscriptionSku.value) {
            message.error('请选择SKU');
            return;
          }
          data.subscription = {
            kind_product_id: props.product.kind_product_id,
            sku_id: selectedSubscriptionSku.value.id,
            num: buyNum.value,
          }
        }
        const response = await request.post('/trade/buy_now', data);

        if (response.code !== 'success') {
          message.error(response.msg)
          return
        }

        payQRCodeUrl.value = response.data.pay_url;
        tradeNo.value = response.data.trade_no;
        payModalVisible.value = true;

        // 开始轮询支付状态
        startCheckPayStatus();
      } catch (err) {
        console.error('支付请求失败:', err);
        message.error('支付请求失败，请稍后重试');
      }
    };

    // 开始轮询支付状态
    const startCheckPayStatus = () => {
      if (!tradeNo.value) {
        return;
      }
      checkPayInterval.value = setInterval(async () => {
        try {
          const response = await request.get(`/trade/check_pay?trade_no=${tradeNo.value}`);
          const tradeState = response.data.state;

          if (tradeState !== 'created') {
            clearInterval(checkPayInterval.value);
            if (tradeState === 'paid') {
              message.success('支付成功');
            } else if (tradeState === 'canceled') {
              message.error('支付已取消');
            }
            payModalVisible.value = false;
            // 这里可以添加支付成功后的逻辑，比如跳转页面等
            emit('usableChange')
          }
        } catch (err) {
          console.error('检查支付状态失败:', err);
        }
      }, 2000);
    };

    // 组件挂载时获取数据
    onMounted(() => {
      fetchPayMethods();
      calculateGain();
    });

    // 组件卸载时清除定时器
    onUnmounted(() => {
      if (checkPayInterval.value) {
        clearInterval(checkPayInterval.value);
      }
    });

    return {
      buyNum,
      calculateGain,
      gain,
      payMethods,
      payModalVisible,
      payQRCodeUrl,
      handlePayMethodClick,
      imageErr,
      selectedPointSku,
      selectedSubscriptionSku,
      handlePointSkuChange,
      handleSubscriptionSkuChange
    };
  }
});
</script>

<style scoped></style>
